import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Ruler, Calculator } from "lucide-react";
import api from "@/lib/api";
import { TYPE_META, formatEuro } from "@/lib/resources";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ResourceFormModal from "@/components/ResourceFormModal";

const fmtUnits = (v, unit) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  const s = Number.isInteger(n) ? String(n) : n.toLocaleString("es-ES", { maximumFractionDigits: 3 });
  return unit ? `${s} ${unit}` : s;
};

export default function ActivityResourcesDialog({ open, onOpenChange, activity, activities, onChanged }) {
  const [data, setData] = useState(null); // {resources, related_units, related_unit, subtotal_venta}
  const [catalog, setCatalog] = useState([]);
  const [units, setUnits] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [resOpen, setResOpen] = useState(false);
  const [drafts, setDrafts] = useState({}); // ar_id -> manual string

  const load = useCallback(() => {
    if (!activity) return;
    Promise.all([
      api.get(`/model/activities/${activity.activity_id}/resources`),
      api.get("/resources"),
      api.get("/resource-units"),
      api.get("/contacts"),
    ]).then(([d, r, u, c]) => {
      setData(d.data);
      setCatalog(r.data);
      setUnits(u.data);
      setContacts(c.data);
      setDrafts(Object.fromEntries((d.data.resources || []).map((x) => [x.activity_resource_id, x.manual_units ?? ""])));
    }).catch(() => toast.error("No se pudieron cargar los recursos de la actividad."));
  }, [activity]);

  useEffect(() => { if (open && activity) load(); }, [open, activity, load]);

  const addFromCatalog = async (rid) => {
    try {
      await api.post(`/model/activities/${activity.activity_id}/resources`, { source_resource_id: rid, manual_units: null });
      load(); onChanged?.(); toast.success("Recurso añadido a la actividad.");
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo añadir."); }
  };

  const commitManual = async (row) => {
    const raw = drafts[row.activity_resource_id];
    const value = raw === "" || raw === null || raw === undefined ? null : Number(raw);
    if (value !== null && Number.isNaN(value)) { toast.error("Valor de uds no válido."); return; }
    if ((row.manual_units ?? null) === value) return;
    try {
      await api.put(`/model/activity-resources/${row.activity_resource_id}`, { manual_units: value });
      load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo actualizar las uds."); }
  };

  const remove = async (arId) => {
    try { await api.delete(`/model/activity-resources/${arId}`); load(); onChanged?.(); toast.success("Recurso quitado."); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo quitar."); }
  };

  const resources = data?.resources || [];
  const subtotal = data?.subtotal_venta ?? 0;
  const relatedLabel = useMemo(() => fmtUnits(data?.related_units, data?.related_unit), [data]);

  if (!activity) return null;
  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="activity-resources-dialog">
          <DialogHeader>
            <DialogTitle>CÓMO · Recursos de «{activity.name}»</DialogTitle>
            <DialogDescription>Recursos necesarios para ejecutar esta actividad. El coste es un vínculo vivo al catálogo.</DialogDescription>
          </DialogHeader>

          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary/60 px-3 py-1.5 text-xs" data-testid="activity-related-units">
              <Ruler className="h-3.5 w-3.5 text-primary" />
              <span className="text-muted-foreground">Uds relacionadas (del área):</span>
              <strong className="text-foreground">{relatedLabel}</strong>
            </div>
            <div className="flex items-center gap-2">
              <Select value="" onValueChange={addFromCatalog}>
                <SelectTrigger className="w-[240px]" data-testid="add-catalog-resource-select"><SelectValue placeholder="Añadir recurso del catálogo…" /></SelectTrigger>
                <SelectContent>
                  {catalog.length === 0 && <div className="px-3 py-2 text-sm text-muted-foreground">No hay recursos en el catálogo.</div>}
                  {catalog.map((r) => (
                    <SelectItem key={r.resource_id} value={r.resource_id} data-testid={`add-catalog-option-${r.resource_id}`}>
                      {r.name} · {r.type} · {formatEuro(r.costs?.sale_price ?? 0)}/{r.unit}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button type="button" className="rounded-full gap-1 shrink-0" onClick={() => setResOpen(true)} data-testid="activity-new-resource-button"><Plus className="h-4 w-4" /> Crear</Button>
            </div>
          </div>

          {data === null ? (
            <p className="text-sm text-muted-foreground">Cargando…</p>
          ) : resources.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground" data-testid="activity-resources-empty">
              Aún no hay recursos en esta actividad. Añade uno del catálogo o crea uno nuevo.
            </div>
          ) : (
            <div className="rounded-2xl border border-border overflow-hidden" data-testid="activity-resources-list">
              <div className="hidden md:grid grid-cols-[1fr_78px_92px_92px_104px_104px_116px_40px] items-center gap-2 bg-secondary/70 px-4 py-2 text-[11px] uppercase tracking-wide text-muted-foreground font-medium">
                <span>Recurso</span>
                <span className="text-center">Ud rel.</span>
                <span className="text-center">Uds manual</span>
                <span className="text-center">Uds calc.</span>
                <span className="text-right">€ Compra ext.</span>
                <span className="text-right">€ Compra int.</span>
                <span className="text-right">€ Venta</span>
                <span />
              </div>
              <div className="divide-y divide-border">
                {resources.map((r) => {
                  const meta = TYPE_META[r.type];
                  const Icon = meta?.icon;
                  return (
                    <div key={r.activity_resource_id} data-testid={`activity-resource-${r.activity_resource_id}`}
                      className="grid grid-cols-1 md:grid-cols-[1fr_78px_92px_92px_104px_104px_116px_40px] items-center gap-2 px-4 py-3 bg-card">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium truncate">{r.name}</span>
                          {meta && <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium ${meta.className}`}>{Icon && <Icon className="h-3 w-3" />} {r.type}</span>}
                        </div>
                        <div className="text-[11px] text-muted-foreground font-mono mt-0.5">
                          {formatEuro(r.sale_price)}/{r.unit || "ud"} · venta
                          {r.missing && <span className="text-destructive ml-1">· recurso no disponible</span>}
                        </div>
                        {r.type === "Tarea" && r.protocol && <p className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap line-clamp-3">{r.protocol}</p>}
                      </div>
                      <div className="text-center text-sm text-muted-foreground font-mono md:block">
                        <span className="md:hidden text-[10px] uppercase text-muted-foreground mr-1">Ud rel.:</span>{fmtUnits(r.related_units, r.related_unit)}
                      </div>
                      <div className="flex items-center justify-center">
                        <Input
                          type="number" step="0.01" inputMode="decimal"
                          className="h-9 w-24 text-center font-mono"
                          placeholder={r.related_units ?? "—"}
                          value={drafts[r.activity_resource_id] ?? ""}
                          onChange={(e) => setDrafts((d) => ({ ...d, [r.activity_resource_id]: e.target.value }))}
                          onBlur={() => commitManual(r)}
                          onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                          data-testid={`manual-units-${r.activity_resource_id}`}
                          aria-label="Uds manual"
                        />
                      </div>
                      <div className="text-center text-sm font-mono font-medium">
                        <span className="md:hidden text-[10px] uppercase text-muted-foreground mr-1">Calc.:</span>{fmtUnits(r.calculated_units, r.related_unit)}
                      </div>
                      <div className="text-right text-sm font-mono" data-testid={`subtotal-externa-${r.activity_resource_id}`}>
                        <span className="md:hidden text-[10px] uppercase text-muted-foreground mr-1">Ext.:</span>{formatEuro(r.subtotal_externa)}
                      </div>
                      <div className="text-right text-sm font-mono" data-testid={`subtotal-interna-${r.activity_resource_id}`}>
                        <span className="md:hidden text-[10px] uppercase text-muted-foreground mr-1">Int.:</span>{formatEuro(r.subtotal_interna)}
                      </div>
                      <div className="text-right text-sm font-mono font-semibold text-[#C75B28]" data-testid={`subtotal-venta-${r.activity_resource_id}`}>
                        <span className="md:hidden text-[10px] uppercase text-muted-foreground mr-1">Venta:</span>{formatEuro(r.subtotal_venta)}
                      </div>
                      <div className="flex justify-end">
                        <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => remove(r.activity_resource_id)} data-testid={`remove-ares-${r.activity_resource_id}`} aria-label="Quitar"><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-x-8 gap-y-1 bg-[#C75B28]/10 px-4 py-3 border-t border-[#C75B28]/20">
                <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#C75B28] sm:mr-auto"><Calculator className="h-4 w-4" /> SubTotales de la actividad</span>
                <span className="text-sm font-mono" data-testid="activity-subtotal-externa">Compra ext.: <strong>{formatEuro(data.subtotal_externa)}</strong></span>
                <span className="text-sm font-mono" data-testid="activity-subtotal-interna">Compra int.: <strong>{formatEuro(data.subtotal_interna)}</strong></span>
                <span className="text-sm font-mono font-bold text-[#C75B28]" data-testid="activity-subtotal-venta">Venta: {formatEuro(subtotal)}</span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ResourceFormModal open={resOpen} onOpenChange={setResOpen} units={units} contacts={contacts} resources={catalog} activities={activities}
        defaultType="Material" onSaved={async (saved) => { setResOpen(false); if (saved?.resource_id) { await addFromCatalog(saved.resource_id); } }}
        onContactCreated={(c) => setContacts((l) => [...l, c])} resource={null} />
    </>
  );
}
